import { useRef } from "react";

export const usePrintRADComponent = ({
  title = document.title,
  onBeforePrint,
  copyStyles = true,
  delayMs = 300, // small safety delay after resources report ready
  mode = "iframe",
  keepOpen = false,
} = {}) => {
  const componentRef = useRef(null);
  const isPrintingRef = useRef(false);

  const buildSkeleton = (doc) => {
    doc.open();
    doc.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title></head><body></body></html>`
    );
    doc.close();
  };

  const copyHostStyles = (doc) => {
    if (!copyStyles) return [];
    const head = doc.head;
    const created = [];

    // base (keeps relative URLs working)
    const base = document.querySelector("base");
    if (base?.href) {
      const b = doc.createElement("base");
      b.setAttribute("href", base.href);
      head.appendChild(b);
      created.push(b);
    }

    // copy <link rel="stylesheet"> and <style>
    const nodes = Array.from(
      document.querySelectorAll('link[rel="stylesheet"], style')
    );
    nodes.forEach((node) => {
      if (node.tagName.toLowerCase() === "link") {
        const href = node.getAttribute("href");
        if (!href) return;
        const link = doc.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        head.appendChild(link);
        created.push(link);
      } else {
        const style = doc.createElement("style");
        style.textContent = node.innerHTML;
        head.appendChild(style);
        created.push(style);
      }
    });

    // minimal print defaults (helps SVGs scale sanely)
    const fix = doc.createElement("style");
    fix.textContent = `
      @page { margin: 12mm; }
      html, body { padding: 0; margin: 0; }
      svg { max-width: 100%; height: auto; }
      @media print { body * { visibility: visible !important; } }
    `;
    head.appendChild(fix);
    created.push(fix);

    return created;
  };

  const waitForResources = (doc) => {
    const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'));
    const linkPromises = links.map(
      (lnk) =>
        new Promise((res) => {
          if (lnk.sheet) return res(); // already loaded
          lnk.addEventListener("load", res, { once: true });
          lnk.addEventListener("error", res, { once: true });
        })
    );

    const fontsPromise =
      doc.fonts && doc.fonts.ready ? doc.fonts.ready : Promise.resolve();

    return Promise.all([...linkPromises, fontsPromise]);
  };

  const mountContent = (doc, html) => {
    const root = doc.body;
    const wrap = doc.createElement("div");
    wrap.id = "print-root";
    // Keep it simple: let your existing CSS size things
    wrap.innerHTML = html;
    root.appendChild(wrap);
  };

  const printNow = (win) => {
    try {
      win.focus();
      win.print();
    } catch {
      // noop
    }
  };

  const printComponent = () => {
    if (!componentRef.current || isPrintingRef.current) return;
    isPrintingRef.current = true;

    try {
      onBeforePrint?.();
    } catch {
      // noop
    }

    if (mode === "iframe") {
      const iframe = document.createElement("iframe");
      Object.assign(iframe.style, {
        position: "fixed",
        right: "0",
        bottom: "0",
        width: "0",
        height: "0",
        border: "0",
        visibility: "hidden",
      });
      document.body.appendChild(iframe);

      const win = iframe.contentWindow;
      const doc = win.document;

      buildSkeleton(doc);
      copyHostStyles(doc);
      mountContent(doc, componentRef.current.innerHTML);

      const cleanup = () => {
        try {
          document.body.removeChild(iframe);
        } catch {
          /* noop */
        }
        isPrintingRef.current = false;
      };

      waitForResources(doc)
        .then(() => new Promise((r) => setTimeout(r, delayMs)))
        .then(() => {
          win.addEventListener("afterprint", cleanup, { once: true });
          printNow(win);
          // fallback cleanup (Safari sometimes skips afterprint)
          setTimeout(cleanup, delayMs + 600);
        })
        .catch(() => cleanup());

      return;
    }

    // window mode (if you ever need a visible preview window)
    const win = window.open("", "PRINT", "height=800,width=1000");
    if (!win) {
      isPrintingRef.current = false;
      return;
    }
    const doc = win.document;

    buildSkeleton(doc);
    copyHostStyles(doc);
    mountContent(doc, componentRef.current.innerHTML);

    const finish = () => {
      try {
        win.close();
      } catch {
        /* noop */
      }
      isPrintingRef.current = false;
    };

    waitForResources(doc)
      .then(() => new Promise((r) => setTimeout(r, delayMs)))
      .then(() => {
        printNow(win);
        if (!keepOpen) setTimeout(finish, delayMs + 600);
      })
      .catch(() => {
        if (!keepOpen) finish();
      });
  };

  return [componentRef, printComponent];
};

export default usePrintRADComponent;
